import type { WordListSubHandler } from '@/teoCore/text/WordListHandler/WordListSubHandler';
import { useTeoEventObserver } from '@/utils/use-teo-event-observer.svelte';

import type { ImprovementProps } from '../../pills/Improvement.svelte';
import type { AssistantVariant } from './AiAssistantCard.svelte';

type CardProps = ReturnType<typeof uiWordListEntriesToCardProps>[number];
export function useCards(wordLists: WordListSubHandler) {
  return useTeoEventObserver({
    initialValue: uiWordListEntriesToCardProps(wordLists),
    update: () => uiWordListEntriesToCardProps(wordLists),
    target: wordLists.events,
    events: ['currentEntriesChanged'],
  });
}

export function useFocusedEntry(wordLists: WordListSubHandler) {
  return useTeoEventObserver({
    initialValue: wordLists.focusedEntry,
    update: () => wordLists.focusedEntry,
    target: wordLists.events,
    events: ['entryFocused'],
  });
}

export function useOnDimensionChange(wordLists: WordListSubHandler) {
  return (dimension: string | undefined) => {
    if (!dimension) {
      wordLists.unFocusEntry();
      return;
    }
    const entry = wordLists.getEntryByRelation(dimension);
    if (entry) {
      wordLists.clickEntry(entry);
    } else {
      wordLists.unFocusEntry();
    }
  };
}

export function useAssistantState(wordLists: WordListSubHandler) {
  return {
    cards: useCards(wordLists),
    focusedEntry: useFocusedEntry(wordLists),
    onDimensionChange: useOnDimensionChange(wordLists),
  };
}

export function toExpandableCards(entries: CardProps[]) {
  return entries.map((entry) => ({
    ...entry,
    id: entry.key,
    disabled: false,
  }));
}

function uiWordListEntriesToCardProps(wordLists: WordListSubHandler) {
  return wordLists.currentEntries.map((entry, index) => ({
    id: 'id-' + index,
    ...entry,
    improvements: entry.suggestions as Omit<ImprovementProps, 'onclick'>[],
    variant: entry.variant as AssistantVariant,
    onRemoveAiSuggestion: (index: number | undefined) => {
      if (index === undefined) return;
      const currentEntry = wordLists.focusedEntry;
      if (currentEntry) {
        currentEntry.aiSuggestions?.splice(index, 1);

        wordLists.currentEntries = [...wordLists.currentEntries];
      }
    },
  }));
}
